import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  addCivilDays,
  calendarCivil,
  civilKey,
  civilParts,
  formatConfirmation,
  formatPreferTimeSlot,
  formatSlotTime,
  slotKey,
  timeZoneCaption,
  timeZoneOptions,
  userTimeZone,
  zonedDateTime,
} from "./demoCallTimeZone.js";

export {
  formatConfirmation,
  formatPreferTimeSlot,
  formatSlotTime,
  slotKey,
  timeZoneCaption,
  timeZoneOptions,
  userTimeZone,
};

export const AVAILABILITY_ZONE = "Australia/Sydney";
export const SLOT_MINUTES = 10;
export const DAY_START_MINUTES = 10 * 60;
export const DAY_END_MINUTES = 17 * 60;
export const LEAD_MINUTES = 30;
export const HORIZON_DAYS = 30;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s()-]+$/;

let slotCacheKey = "";
let slotCache = new Map();

function isSydneyWeekday(parts) {
  const day = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  return day >= 1 && day <= 5;
}

function sydneySlots(now) {
  const today = civilParts(now, AVAILABILITY_ZONE);
  const cutoff = new Date(now.getTime() + LEAD_MINUTES * 60 * 1000);
  const slots = [];

  for (let offset = 0; offset < HORIZON_DAYS; offset += 1) {
    const day = addCivilDays(today, offset);
    if (!isSydneyWeekday(day)) continue;

    for (
      let minutes = DAY_START_MINUTES;
      minutes + SLOT_MINUTES <= DAY_END_MINUTES;
      minutes += SLOT_MINUTES
    ) {
      const slot = zonedDateTime(
        day.year,
        day.month - 1,
        day.day,
        Math.floor(minutes / 60),
        minutes % 60,
        AVAILABILITY_ZONE,
      );
      if (slot < cutoff) continue;
      slots.push(slot);
    }
  }

  return slots;
}

function slotsByDisplayDate(now, displayZone) {
  const key = `${now.getTime()}|${displayZone}`;
  if (key === slotCacheKey) return slotCache;

  const grouped = new Map();
  sydneySlots(now).forEach((slot) => {
    const day = civilKey(civilParts(slot, displayZone));
    const bucket = grouped.get(day);
    if (bucket) bucket.push(slot);
    else grouped.set(day, [slot]);
  });

  slotCacheKey = key;
  slotCache = grouped;
  return grouped;
}

export function slotsForDate(date, now = new Date(), displayZone = userTimeZone()) {
  const bucket = slotsByDisplayDate(now, displayZone).get(civilKey(calendarCivil(date)));
  return bucket ? bucket.slice() : [];
}

export function isSelectableDate(date, now = new Date(), timeZone = userTimeZone()) {
  return slotsForDate(date, now, timeZone).length > 0;
}

export function isSlotAvailable(slot, now = new Date(), timeZone = userTimeZone()) {
  if (!slot) return false;
  const civil = civilParts(slot, timeZone);
  const day = new Date(civil.year, civil.month - 1, civil.day);
  return slotsForDate(day, now, timeZone).some((candidate) => candidate.getTime() === slot.getTime());
}

export function formatDayLabel(date) {
  return format(date, "EEEE, MMMM d, yyyy");
}

export function dayKey(date) {
  return format(date, "yyyy-MM-dd");
}

export function formatMonthLabel(date) {
  return format(date, "MMMM yyyy");
}

export function calendarWeeks(monthDate) {
  const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start, end });
  const weeks = [];

  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }

  return weeks;
}

export function isMonthReachable(monthDate, now, timeZone = userTimeZone()) {
  const monthStart = civilKey({
    year: monthDate.getFullYear(),
    month: monthDate.getMonth() + 1,
    day: 1,
  });
  const monthEnd = civilKey({
    year: monthDate.getFullYear(),
    month: monthDate.getMonth() + 1,
    day: endOfMonth(monthDate).getDate(),
  });
  const today = civilParts(now, timeZone);
  const horizon = addCivilDays(today, HORIZON_DAYS - 1);
  return monthEnd >= civilKey(today) && monthStart <= civilKey(horizon);
}

export function shiftMonth(monthDate, delta) {
  return startOfMonth(addMonths(monthDate, delta));
}

export function isOptionalWebsite(value) {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (/\s/.test(trimmed)) return false;

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(withProtocol);
    const host = url.hostname;
    return host.includes(".") && !host.startsWith(".") && !host.endsWith(".");
  } catch {
    return false;
  }
}

export function validateDemoCall(
  { fullName, workEmail, mobile, website, slot },
  now = new Date(),
  timeZone = userTimeZone(),
) {
  if (!isSlotAvailable(slot, now, timeZone)) return "slot";
  if (!fullName.trim()) return "name";
  if (!EMAIL_RE.test(workEmail.trim())) return "email";
  const phone = mobile.trim();
  if (!PHONE_RE.test(phone) || phone.replace(/\D/g, "").length < 8) return "mobile";
  if (!isOptionalWebsite(website)) return "website";
  return "";
}
